// Every string BidSheet can render, and the shape a caller may replace them with.
//
// The mechanism is BidCard's exactly — one object of finished text in one
// language, merged over these defaults key by key, with an UNKNOWN key rejected
// because that is the failure that renders English forever with nothing saying
// why. The reasoning is written out once, next door in bidCardTranslations.js.
//
// What is worth saying here is which keys the tray has that the card does not,
// and why each exists: the primary's three verbs and its confirm, the increment
// ladder's label, the numeric field's four, the standing-order hint, and the
// sentence naming the price when there is no bid to name.
//
// Deliberately NOT exported from the package barrel: the catalog gate holds
// that a barrel exports components and nothing else.

import { unreadTranslationKeys as unreadKeysAgainst } from './bidCardTranslations';

export { formatMoneyDefault, interpolate } from './bidCardTranslations';

/**
 * The English defaults, frozen.
 *
 * `{name}` placeholders are filled by the tray. A translation may move them
 * around its sentence or drop one it does not need; it may not invent one.
 */
export const BID_SHEET_TRANSLATIONS = Object.freeze({
  // ── The asset header ─────────────────────────────────────────────────────
  // One line, because the run and the lane are read together — and the
  // separator is part of the string, since where it goes is the translation's
  // decision. `run` labels the dealer-facing number, the one place this
  // component does not say asset.
  runLane: 'Run {run} · {lane}',
  // The run alone, for an asset the sale has not assigned a lane.
  run: 'Run {run}',
  // On the thumbnail of an asset that is on the block now.
  liveChip: 'LIVE',
  // The clock, by magnitude rather than by sale type — BidCard's three, so the
  // same asset reads the same on the card behind the tray and in it.
  secondsLeft: '{seconds}s left',
  minutesLeft: '{minutes}m {seconds}s',
  closesIn: 'Closes in {hours}h {minutes}m',

  // ── The marks row ────────────────────────────────────────────────────────
  notes: 'NOTES',
  // BidCard's word, deliberately: the card and the tray behind it read the same
  // word for the same fact.
  liked: 'LIKED',
  // Visually hidden. Colored letters are not self-describing.
  lights: 'Lights: {lights}',
  lightNames: Object.freeze({ R: 'Red', G: 'Green', Y: 'Yellow', B: 'Blue', W: 'White' }),

  // ── The money ────────────────────────────────────────────────────────────
  // The word beneath the figure is fixed, exactly as on the card — and `final`
  // DROPS it rather than swapping it, which is why there is no `priceLabel`
  // prop and no second word here to swap in.
  moneyLabel: 'current',
  // An asset that has not opened has no current bid, and the tray says so
  // rather than rendering a figure it cannot compute a bid from.
  priceNotSet: 'No bid yet',
  // Visually hidden, beside the dealer's own ceiling from the worksheet.
  target: 'Target {amount}',

  // ── The two tabs ─────────────────────────────────────────────────────────
  liveBid: 'Live bid',
  proxy: 'Proxy',
  // The proxy tab's accessible name, for a language that has to ABBREVIATE to
  // fit the tab: fr-CA draws `Auto`, and the name spells the action out, which
  // is the one thing the visible word cannot then do.
  //
  // `null`, and NOT the English word. An accessible name must contain the
  // visible text (WCAG 2.5.3, and axe's label-content-name-mismatch), so a
  // default that says `Proxy` over a tab a caller has translated to
  // `Automatique` is a violation this component would ship on every consumer
  // that translates the label and not the name. Absent, the tab is named by
  // what it says, which is correct in every language that does not abbreviate.
  proxyAria: null,
  // The live tab's `aria-describedby` while a proxy can still fire. It was a
  // rendered paragraph and cost 85px in one panel and nothing in the other,
  // which broke the height parity the tray is built on. The words survive where
  // they are still reachable; the layout does not carry them.
  proxyLockNotice: 'Your proxy of {price} is bidding this asset. Cancel it to bid live.',

  // ── The increment ladder ─────────────────────────────────────────────────
  increment: 'Increment',
  tapToChange: 'tap to change',

  // ── The live control ─────────────────────────────────────────────────────
  // The VERB, not the noun. "Bid" names the thing a dealer is looking at; this
  // control is the one that spends the money, so it says what pressing it does
  // — and it is the same words Asset Detail's own primary carries.
  placeBid: 'Place bid',
  // The instruction on the slide-to-confirm track, without the figure: the
  // track keeps the separator and the money, because where the money sits is
  // what its clip mask is measured against. The tray opens with the track
  // already armed, so this — and not the button behind it — is what a dealer
  // reads as the primary, which is why it names the action too.
  slidePlaceBid: 'Slide to place bid',
  // The dealer holds the high bid. The tray REPLACES the control with these
  // words rather than graying one out, and they are the component's own — the
  // one absent-control state the caller does not phrase, because the tray is
  // told the fact directly.
  winningBid: 'You have the winning bid',
  // `disabled: {}` is a complete tray.
  bidUnavailable: 'Bidding unavailable',
  // Shown whenever the live control is away and the sale can still resume. Not
  // keyed to a pause, because the tray is never told WHY it cannot bid.
  standingOrderHint: 'Set a proxy and it will bid for you when this resumes.',

  // ── The primary, which is one control with three readings ────────────────
  setProxy: 'Set proxy',
  updateProxy: 'Update proxy',
  cancelProxy: 'Cancel proxy',
  cancelProxyConfirm: 'Tap again to cancel',
  cancelProxyCaveat: 'Stops future auto-bids. A bid already placed still stands.',

  // ── The numeric field ────────────────────────────────────────────────────
  // At a $100 increment, stepping $25,300 to $28,600 is 33 taps, and a dealer
  // does not have 33 taps while an auctioneer is calling.
  typeAmount: 'Type a number',
  closeKeypad: 'Close',
  // Commits to the DRAFT, not to the engine — the ceiling is still submitted
  // from the resting control, because one extra zero is a ten-times ceiling.
  useAmount: 'Use this amount',
  // Read out as the dealer types, which is the whole point of it: a ceiling at
  // or under the live price is ignored by the engine and fires nothing, and
  // that is the one failure in this component that used to say nothing.
  belowFloor: 'below {amount}',

  // ── Bidding as ───────────────────────────────────────────────────────────
  biddingAs: 'Bidding as',
  // The chip's second line. The account is what disambiguates two dealerships
  // sharing a trading name, so it is never dropped in favor of the name.
  dealershipMeta: '{role} · #{account}',
  // The chip's first line for an identity the record gives no name — the
  // server falls back to the bare badge for one. The badge stays on the second
  // line, so the chip never says the same number twice.
  unnamedDealership: 'Bidder',
});

/**
 * The keys in `translations` that BidSheet would not read, as dotted paths.
 *
 * The walk itself is BidCard's — it already takes the defaults object as an
 * argument, so the tray passes its own rather than owning a second copy of a
 * recursive comparison that has to behave identically on both surfaces.
 */
export const unreadTranslationKeys = (translations) =>
  unreadKeysAgainst(translations, BID_SHEET_TRANSLATIONS);

/** One language's text over the English defaults, key by key. */
export function mergeTranslations(translations) {
  if (!translations) return BID_SHEET_TRANSLATIONS;

  return {
    ...BID_SHEET_TRANSLATIONS,
    ...translations,
    lightNames: { ...BID_SHEET_TRANSLATIONS.lightNames, ...(translations.lightNames ?? {}) },
  };
}

/**
 * The `i18n` prop's validator: `{ translations, formatMoney }`, both optional.
 *
 * Closed on the outer object too — `translation` for `translations` fails
 * exactly the way a mis-keyed string does, silently and in English.
 */
export function isValidBidSheetI18n(i18n) {
  if (i18n === null || typeof i18n !== 'object') return false;
  if (Object.keys(i18n).some((key) => key !== 'translations' && key !== 'formatMoney')) return false;
  if (i18n.formatMoney !== undefined && typeof i18n.formatMoney !== 'function') return false;
  if (i18n.translations === undefined) return true;
  return unreadTranslationKeys(i18n.translations).length === 0;
}
